import type { BlogPost } from "../blogTypes";

export const sublettingRulesNetherlandsWhatYouNeedToKnow: BlogPost = {
    slug: "subletting-rules-netherlands-what-you-need-to-know",
    title: "Subletting Rules in the Netherlands: What You Need to Know",
    metaTitle: "Subletting Rules Netherlands | Can You Sublet Your Rental?",
    metaDescription: "Can you sublet your rental in the Netherlands? Learn the rules for onderhuur, when it's allowed, risks of unauthorized subletting, and what your contract says.",
    excerpt: "Thinking about subletting your apartment while you travel or move? Dutch subletting rules are strict and depend on your contract — here's what to check before listing on Airbnb or finding a subtenant.",
    date: "2026-01-02",
    updated: "2026-08-23",
    readTime: "4 min read",
    category: "Rental Law",
    keywords: ["subletting Netherlands", "onderhuur regels", "sublet rental Dutch law", "Airbnb subletting Netherlands"],
    content: `
## Is Subletting Allowed in the Netherlands?

The short answer is that the rules depend on whether the whole home or only a room is sublet, what the main lease says, and which municipal rules apply. Unauthorized subletting can be a serious breach and can lead the landlord to ask a court to end the tenancy.

## The Legal Framework

Dutch law distinguishes between two types of subletting:

1. **Full subletting** (gehele onderhuur) — you sublet the entire property and move out temporarily
2. **Partial subletting** (gedeeltelijke onderhuur) — you sublet a room while still living there

### Full Subletting

**Legal rule:** Subletting the whole home without permission is prohibited and can lead to a court ending your main tenancy. If your contract explicitly allows it, or your landlord gives **written permission**, full subletting may be possible, but you must still comply with local municipality occupancy rules.

If you fully sublet without permission, your landlord can:
- Ask the court to terminate your contract
- Demand that you and the subtenant leave
- Pursue legal action for breach of contract

### Partial Subletting

Dutch government guidance distinguishes partial subletting from subletting the whole home. A tenant who keeps their main residence in an independent home may generally sublet a room unless the main lease prohibits it. Obtain written consent where the contract requires it, and separately check municipal occupancy, room-letting and registration rules. Do not assume that a room can be sublet if the contract forbids it. [Read the government's subletting guidance](https://www.rijksoverheid.nl/vraag-en-antwoord/woning-verhuren/wat-moet-ik-weten-als-ik-mijn-huis-of-kamer-wil-onderverhuren).

## Airbnb and Short-Term Rentals

Short-term subletting through platforms like Airbnb has additional rules:

- Registration, permit and advance-reporting rules differ by municipality. Check the rules for the exact address; do not assume that every or most Dutch municipalities use the same registration system.
- Amsterdam generally limits holiday letting of a main home to **30 nights per calendar year**. Since 1 April 2026, the limit is **15 nights** in eight specified areas of Centrum and De Pijp, with a permit, registration, advance reporting and a maximum of four guests required. [See Amsterdam's current holiday-letting rules](https://www.amsterdam.nl/wonen-bouwen-verbouwen/huisvestingsvergunning/vergunning-vakantieverhuur-aanvragen/) and the [15-night areas in force from 1 April 2026](https://www.amsterdam.nl/wonen-bouwen-verbouwen/huisvestingsvergunning/vergunning-vakantieverhuur-aanvragen/regels-vakantieverhuur-deel-centrum-en/)
- Other municipalities set their own limits, so always check locally
- Tourist-tax duties depend on the municipality and may also depend on how the booking platform handles collection. Amsterdam requires tourist tax for holiday letting; check the relevant municipality for other locations.
- Your landlord's permission is still required
- Your insurance may not cover short-term guests

## Risks of Unauthorized Subletting

Subletting without permission can lead to serious consequences:

- **Contract termination** — a court can end your lease if the landlord applies
- **Fines** — municipalities can fine you for unregistered short-term rentals
- **Deposit deductions** — a landlord can only deduct permitted statutory categories (such as damage you caused or unpaid rent) from your deposit, and must substantiate the deduction; a deposit is not automatically forfeited
- **Legal liability** — you're responsible for any damage caused by your subtenant

## What Protection Does a Subtenant Have?

Protection depends on what was sublet. A subtenant of an **entire independent dwelling** can, in some circumstances, continue occupying it directly from the owner if the main tenancy ends — even where the owner did not consent to the subletting — although the owner can ask the court to end that continuation on specified grounds. A **room subtenant** generally cannot take over the main tenancy when it ends, although contractual claims against the main tenant may still remain. [See the Juridisch Loket's guidance on subtenant rights](https://www.juridischloket.nl/wonen-en-buren/huurwoning/onderhuur/).

## What to Check in Your Contract

Your rental contract should address subletting. Look for:

- A specific **subletting clause** (onderhuurclausule)
- Whether permission is required and how to obtain it
- Any restrictions on the type or duration of subletting
- Consequences stated for unauthorized subletting

## How to Get Permission

If you want to sublet:

1. **Review your contract** for subletting terms
2. **Write to your landlord** requesting permission — always in writing
3. **Provide details** about the subtenant and duration
4. **Get written approval** before proceeding
5. **Create a subletting agreement** with your subtenant
6. **Register with the municipality** if required

## Check Your Subletting Rights

Wondering what your contract says about subletting? [Upload it to RentSense NL](/app) for €7.99 and get an analysis, developed with reference to current official Dutch government and Huurcommissie sources, that explains your subletting clause in plain English.

## Sources checked

- [Government: subletting your home or a room](https://www.rijksoverheid.nl/vraag-en-antwoord/woning-verhuren/wat-moet-ik-weten-als-ik-mijn-huis-of-kamer-wil-onderverhuren)
- [Juridisch Loket: subtenant rights](https://www.juridischloket.nl/wonen-en-buren/huurwoning/onderhuur/)
- [Amsterdam: holiday-letting permit rules](https://www.amsterdam.nl/wonen-bouwen-verbouwen/huisvestingsvergunning/vergunning-vakantieverhuur-aanvragen/)
- [Amsterdam: 15-night areas from 1 April 2026](https://www.amsterdam.nl/wonen-bouwen-verbouwen/huisvestingsvergunning/vergunning-vakantieverhuur-aanvragen/regels-vakantieverhuur-deel-centrum-en/)
    `,
};
